import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";
import { cn } from "../../../utils";
import { Button } from "../../base/button/button";
import { Card } from "../../base/card/card";
/**
 * The two shells every rich tool part draws itself in: a card for the content
 * and a muted notice for the states where there is none. They live here rather
 * than per renderer because a chart, an export and an embedded view must be
 * indistinguishable in the transcript — three copies drifted apart once already.
 */ /**
 * A rich part never widens the transcript and never collapses inside it: the
 * assistant bubble is `ghost`, which is full-width and overflow-visible, so this
 * card takes the message column's width and clips nothing. Recharts'
 * `ResponsiveContainer` has no intrinsic width of its own — it fills what it is
 * given — so the full-width shell is what makes a chart visible at all, not a
 * nicety.
 */ export function ChatPartCard({ children, className, role, label }) {
    return /*#__PURE__*/ _jsx(Card, {
        className: cn("w-full min-w-0 max-w-full", className),
        role: role,
        "aria-label": label,
        children: children
    });
}
/**
 * The "this did not load — try it again" line.
 *
 * The frame is the only thing its two callers disagree on, so the frame is what
 * `className` carries: a block-level part passes the dashed box, while the
 * hover card on a mention chip passes nothing, being already inside a popover
 * where a second border reads as a broken card. Everything else — the muted
 * message, the gap, the icon, the label — must not differ between them, and a
 * hand-written second copy is how it would.
 *
 * The retry LABEL is owned here rather than at either caller: it is one word for
 * one gesture, and the message beside it is what names the thing that failed.
 */ export function ChatRetryRow({ message, onRetry, className }) {
    const { t } = useTranslation();
    return /*#__PURE__*/ _jsxs("p", {
        className: cn("flex w-full min-w-0 items-center gap-3 text-sm text-muted-foreground", className),
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "min-w-0 flex-1",
                children: message
            }),
            onRetry && /*#__PURE__*/ _jsxs(Button, {
                variant: "ghost",
                size: "small",
                onClick: onRetry,
                children: [
                    /*#__PURE__*/ _jsx(RotateCcw, {
                        "aria-hidden": true
                    }),
                    t("chat.retry")
                ]
            })
        ]
    });
}
/**
 * A part that could not be produced, or whose payload does not hold, or whose
 * record is no longer visible. Deliberately small and muted: a failed tool call
 * must never break the answer around it.
 *
 * `onRetry` is offered only where there is something to repeat — a failed fetch.
 * A part that itself could not be read has no retry, and neither does a record
 * the reader may no longer see.
 */ export function ChatPartNotice({ message, onRetry }) {
    return /*#__PURE__*/ _jsx(ChatRetryRow, {
        message: message,
        onRetry: onRetry,
        className: "rounded-md border border-dashed px-3 py-2"
    });
}

//# sourceMappingURL=chat-part-shell.js.map